import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// The glyph sits inside the field's border, not beside it: the box owns the border and the input is
// transparent and borderless within it. Putting the icon next to a bordered input instead — which is
// what happens if you reach for an input group — reads as two controls.
const Box = styled.div`
    display: flex;
    width: ${(props) => props.$width};
    align-items: center;
    gap: var(--gap-m);
    min-width: 0;
    /* An exact height, not a floor: a single-line field one pixel taller than the button beside it is
       exactly the misalignment the shared height exists to prevent. */
    height: var(--pw-control-height);
    padding: 0 11px;
    border: 1px solid var(--border-strong);
    border-radius: var(--pw-radius-lg);
    background: var(--surface-card);
    transition: border-color var(--pw-transition-fast) var(--pw-ease);

    &:focus-within {
        border-color: var(--pw-brand-cyan);
    }
`;

const Glyph = styled.i`
    flex: none;
    font-size: var(--pw-text-hint);
    color: var(--pw-neutral-450);
`;

const Field = styled.input`
    flex: 1;
    min-width: 0;
    width: ${(props) => props.$width};
    border: none;
    outline: none;
    background: transparent;
    color: var(--text-heading);
    font-family: inherit;
    font-size: var(--pw-text-control);
    font-weight: var(--pw-weight-light);

    &::placeholder {
        color: var(--text-subtle);
    }

    /* type=search draws its own clear button in WebKit, so the field showed two X next to each other.
       Ours stays, because it is the one that matches the design and sits where the layout puts it. */
    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
        appearance: none;
        -webkit-appearance: none;
    }
`;

// A clear affordance only once there is something to clear.
const Clear = styled.button`
    display: inline-flex;
    align-items: center;
    flex: none;
    padding: 0;
    border: none;
    background: none;
    color: var(--text-muted);
    font-size: var(--pw-text-hint);
    cursor: pointer;

    &:hover {
        color: var(--text-heading);
    }
`;

const PathwaySearchField = ({
    value,
    onChange,
    onSubmit,
    placeholder = "Suchen…",
    ariaLabel,
    width = "190px",
    dataCy,
}) => (
    <Box $width={width} data-cy={dataCy ? `${dataCy}-box` : undefined}>
        <Glyph className="fa fa-magnifying-glass" aria-hidden="true" />
        <Field
            type="search"
            role="searchbox"
            $width={width}
            value={value}
            placeholder={placeholder}
            aria-label={ariaLabel || placeholder}
            data-cy={dataCy}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
                if (event.key === "Enter" && onSubmit) {
                    event.preventDefault();
                    onSubmit(event.target.value);
                }
            }}
        />
        {value ? (
            <Clear
                type="button"
                aria-label="Suche zurücksetzen"
                data-cy={dataCy ? `${dataCy}-clear` : undefined}
                onClick={() => onChange("")}
            >
                <i className="fa fa-xmark" aria-hidden="true" />
            </Clear>
        ) : null}
    </Box>
);

PathwaySearchField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    // Enter is what a search field is expected to answer to; without it the only way to run the search was a
    // separate button, and a field that ignores Enter reads as broken.
    onSubmit: PropTypes.func,
    placeholder: PropTypes.string,
    ariaLabel: PropTypes.string,
    width: PropTypes.string,
    dataCy: PropTypes.string,
};

export default PathwaySearchField;
